import { useMutation } from 'react-query'

import agent from 'agent'
import useAuth from 'hooks/use-auth'
import { type RunTestEndpointResponse } from 'queries/external-integrations/types'
import {
  getEndpointWithParams,
  throwIfEmptyResponse
} from 'queries/queries-utils'
import { statusChecksMutationKeys } from 'queries/status-checks/query-keys'
import { throwIfBadResponseForRunTestEndpoint } from 'queries/status-checks/query-utils'
import {
  type RunTestEndpointParams,
  type UseStatusCheckRunTestEndpointOptions,
  type UseStatusCheckRunTestEndpointReturnType
} from 'queries/status-checks/types'

function runStatusCheckTestEndpoint(
  mutationKey: ReturnType<typeof statusChecksMutationKeys.runTestEndpoint>,
  signal?: AbortSignal
) {
  return async function (requestParams: RunTestEndpointParams) {
    const [endpoint, endpointParams] = mutationKey
    const endpointWithParams = getEndpointWithParams(endpoint, endpointParams)
    const response = await agent.post<RunTestEndpointResponse>(
      endpointWithParams,
      { ...requestParams },
      { signal }
    )

    throwIfEmptyResponse(response)
    throwIfBadResponseForRunTestEndpoint(response)

    return response.data
  }
}

export function useStatusCheckRunTestEndpoint(
  signal?: AbortSignal,
  options?: UseStatusCheckRunTestEndpointOptions
): UseStatusCheckRunTestEndpointReturnType

export function useStatusCheckRunTestEndpoint(
  options?: UseStatusCheckRunTestEndpointOptions
): UseStatusCheckRunTestEndpointReturnType

/**
 * Submits a POST request which validates the configuration of a Status Check or Authentication.
 * Applies specifically to public (non-PNI) integrations.
 */
export function useStatusCheckRunTestEndpoint(
  ...args:
    | [AbortSignal?, UseStatusCheckRunTestEndpointOptions?]
    | [UseStatusCheckRunTestEndpointOptions?]
): UseStatusCheckRunTestEndpointReturnType {
  const { currentTeamId } = useAuth()
  const mutationKey = statusChecksMutationKeys.runTestEndpoint(currentTeamId)
  const [signalOrOptions, maybeOptions] = args
  const hasSignal = signalOrOptions instanceof AbortSignal

  return useMutation({
    ...(maybeOptions ?? signalOrOptions),
    mutationFn: runStatusCheckTestEndpoint(
      mutationKey,
      hasSignal ? signalOrOptions : undefined
    ),
    mutationKey
  })
}
